<!--
 * @Author: huangxuejuan
 * @Date: 2026-09-01 10:00:00
 * @Description: 经营月报卡片（content_metadata ai_agent_card_route=query_mpos_month_report_mcp）
 * 样式还原蓝湖设计稿（lanhu_...14zihaojingyingyuebao）：335px 蓝色渐变卡（#6DA3FF→#5A97FF，圆角10px），
 * 头部无图标（标题+已生成徽章），金额 30px，KPI 行为白色 10% 半透明底贴卡片底部，数值在上标签在下
 * 数据（docs/报文1/2.txt、历史记录.txt 实测 query_mpos_month_report_mcp 链路）：
 * { rows: [{ current_month_trade_amount(元字符串"18600.50"), current_month_success_trade_count,
 *   last_month_trade_amount, last_month_trade_count, mom_change("0"，1升/0平/-1降)、
 *   mom_change_rate(环比幅度，两者均直接取后端值，前端不计算) }],
 *   request_params: { stat_month: "2026-09", ... } }
 * 标题/截至日期取 request_params.stat_month（parseCardMetadata 透传，缺失兜底当月+今天）；
 * 「数据列表.报告文件访问链接」为约定链路字段（实测未下发），存在时点击卡片跳转 H5 报告
-->
<script setup lang="ts">
import { computed } from "vue"
import type { CardPayload } from "@/types/app"
import trendUpIcon from "@/assets/images/agent/cards/trend-up-icon.png"
import trendDownIcon from "@/assets/images/agent/cards/trend-down-icon.png"

const props = defineProps<{
  card: CardPayload
}>()

// rows[0] 月报统计数据
// 金额为「元」字符串（如 "18600.50"）；环比方向/幅度直接取后端 mom_change / mom_change_rate，前端不计算
const row = computed<Record<string, any>>(() => props.card.data?.rows?.[0] || {})

// 成功交易笔数：优先定稿字段名兜底实测字段名（千分位），缺失展示 --
const successCount = computed(() =>
  formatNumber(row.value.current_month_success_trade_count, (num) => num.toLocaleString("zh-CN"))
)

// 报表月份：request_params.stat_month（如 "2026-09"，parseCardMetadata 透传），缺失兜底当月
const statMonth = computed(() => {
  const parsed = parseStatMonth(props.card.data?.request_params?.stat_month)
  if (parsed) return parsed
  const now = new Date()
  return { year: now.getFullYear(), month: now.getMonth() + 1 }
})

// 标题：XXXX年X月经营月报
const reportTitle = computed(() => `${statMonth.value.year}年${statMonth.value.month}月经营月报`)

// 金额标签：本月交易总额（截至x月xx日）——当月报表数据截至今天，历史月份截至该月月末
const amountLabel = computed(() => {
  const now = new Date()
  const { year, month } = statMonth.value
  if (year === now.getFullYear() && month === now.getMonth() + 1) {
    return `本月交易总额（截至${month}月${now.getDate()}日）`
  }
  const lastDay = new Date(year, month, 0).getDate()
  return `本月交易总额（截至${month}月${lastDay}日）`
})

// 本月交易总额：金额为「元」字符串（千分位 + 2 位小数），缺失展示 --
const totalAmount = computed(() =>
  formatNumber(row.value.current_month_trade_amount, (num) =>
    num.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  )
)

// 环比方向：直接取后端 mom_change（1=上升↑ 0=持平 -1=下降↓），缺失/非法不显示箭头，前端不推算
const trendUp = computed<boolean | null>(() => {
  const mom = toNumber(row.value.mom_change)
  if (mom === null) return null
  if (mom > 0) return true
  if (mom < 0) return false
  return null // 持平
})

// 环比幅度：直接取后端 mom_change_rate（兼容 "12.6" / "12.6%" 两种形式，数字统一保留 1 位小数），
// 缺失/非法展示 --，前端不计算
const trendRate = computed(() => {
  const raw = row.value.mom_change_rate
  const str = raw === undefined || raw === null ? "" : String(raw).trim()
  if (!str) return "--"
  if (str.includes("%")) return str
  const num = toNumber(str)
  return num === null ? "--" : `${num.toFixed(1)}%`
})

// 数值兜底解析：后端数值字段均为字符串，空值/非法数字返回 null
function toNumber(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === "") return null
  const num = Number(raw)
  return Number.isNaN(num) ? null : num
}

// 数值格式化：解析成功按 formatter 格式化，否则展示 --
function formatNumber(raw: unknown, formatter: (num: number) => string): string {
  const num = toNumber(raw)
  return num === null ? "--" : formatter(num)
}

// 解析 stat_month（如 "2026-09" / "2026/9"）为年月，非法返回 null
function parseStatMonth(raw: unknown): { year: number; month: number } | null {
  if (typeof raw !== "string") return null
  const match = raw.trim().match(/^(\d{4})[-/年.](\d{1,2})/)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  if (month < 1 || month > 12) return null
  return { year, month }
}
</script>

<template>
  <div class="monthly-report-card">
    <div class="card-header">
      <span class="report-title">{{ reportTitle }}</span>
      <span class="report-status">已生成</span>
    </div>
    <div class="amount-section">
      <div class="amount-label">{{ amountLabel }}</div>
      <div class="amount-value">￥{{ totalAmount }}</div>
    </div>
    <div class="kpi-row">
      <div class="kpi-item">
        <div class="kpi-value">{{ successCount }}笔</div>
        <div class="kpi-label">成功交易笔数</div>
      </div>
      <div class="kpi-divider" />
      <div class="kpi-item">
        <div class="kpi-value">
          {{ trendRate }}
          <img v-if="trendUp !== null" :src="trendUp ? trendUpIcon : trendDownIcon" class="trend-icon" alt="趋势" />
        </div>
        <div class="kpi-label">环比上月</div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* 卡片混排在白底消息框内（answer-wrapper），无需自带描边气泡与阴影 */
.monthly-report-card {
  width: 335px;
  max-width: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: linear-gradient(135deg, #6da3ff 0%, #5a97ff 100%);
  color: #ffffff;

  &.clickable {
    cursor: pointer;
  }
}

.card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 11px 14px 0;
}

.report-title {
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-status {
  flex-shrink: 0;
  font-size: 10px;
  line-height: 13px;
  padding: 2px 3px 1px;
  border-radius: 1px;
  background: rgba(255, 255, 255, 0.2);
}

.amount-section {
  margin: 15px 0 0 14px;
}

.amount-label {
  font-size: 12px;
  line-height: 16px;
}

.amount-value {
  font-size: 30px;
  line-height: 48px;
  margin-top: -5px;
  white-space: nowrap;
  font-weight: bold;
}

/* KPI 行：白色 10% 半透明底，顶部小圆角、贴卡片底部（底部由卡片 overflow 裁出圆角） */
.kpi-row {
  display: flex;
  align-items: center;
  margin-top: 15px;
  padding: 13px 8px 17px 15px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px 6px 0 0;
  background-image: url("@/assets/images/agent/cards/bg-icon.png");
  background-repeat: no-repeat;
  background-position: right bottom;
  background-size: 70px auto;
}

.kpi-item {
  flex: 1;
  min-width: 0;
}

.kpi-value {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 18px;
  line-height: 29px;
  font-weight: 500;
  white-space: nowrap;
}

.kpi-label {
  font-size: 12px;
  line-height: 19px;
  margin-top: -2px;
}

.kpi-divider {
  flex-shrink: 0;
  width: 1px;
  height: 30px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 15px;
}

.trend-icon {
  flex-shrink: 0;
  width: 18px;
  height: 11px;
  object-fit: contain;
}
</style>
